import { useCallback, useEffect, useMemo } from "react";
import { useQueryLoader, useRelayEnvironment } from "react-relay";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import { WidgetTaskCollectionMockPreviewQUERY } from "./WidgetTaskCollectionMockPreview";
import {
  WidgetTaskCollectionMockPreviewQuery,
  WidgetTaskCollectionMockPreviewQuery$rawResponse,
} from "./__generated__/WidgetTaskCollectionMockPreviewQuery.graphql";
import { useIntl } from "react-intl";

const DATA_PREFIX = "WIDGET_TASK_COLLECTION_ADD:";
export const WidgetTaskCollectionPreviewID =
  DATA_PREFIX + "previewWidgetTaskCollection";

export function taskSection({
  index,
  title,
  routineTitle = "Checklista Butik",
}: {
  index: number;
  title: string;
  routineTitle?: string;
}) {
  return {
    __typename: "",
    id: DATA_PREFIX + "taskSection" + index,
    inProgressTaskExecution: {
      __typename: "TaskExecutionCheck",
      __isTaskExecution: "TaskExecutionCheck",
      checkValue: "POSITIVE",
      id: DATA_PREFIX + "taskExecution" + index,
      routineOccasion: {
        endAt: "",
        id: DATA_PREFIX + "routineOccasion",
      },
    },
    section: {
      id: DATA_PREFIX + "section" + index,
      title: null,
    },
    task: {
      __typename: "TaskCheck",
      id: DATA_PREFIX + "task" + index,
      routine: {
        id: DATA_PREFIX + "routine" + index,
        title: routineTitle,
      },
      title,
    },
  } satisfies NonNullable<
    WidgetTaskCollectionMockPreviewQuery$rawResponse["dashboard"]
  >["previewWidgetTaskCollection"]["taskSections"][number];
}

export function useWidgetTaskCollectionPreviewQueryLoader(storecastId: string) {
  const env = useRelayEnvironment();

  const vars = useMemo(
    () =>
      ({
        input: {
          tasks: [],
          id: "",
        },
        storecastId,
      }) as const,
    [storecastId]
  );

  const [queryRef, load] = useQueryLoader<WidgetTaskCollectionMockPreviewQuery>(
    WidgetTaskCollectionMockPreviewQUERY
  );

  const intl = useIntl();

  const populateData = useCallback(() => {
    const alternativeRoutineTitle = intl.formatMessage({
      description: "WATaskCollection alternative checklist title",
      defaultMessage: "Staff Checklist",
    });
    const req = getRequest(WidgetTaskCollectionMockPreviewQUERY);
    const op = createOperationDescriptor(req, vars);
    const payload: WidgetTaskCollectionMockPreviewQuery$rawResponse = {
      dashboard: {
        id: vars.storecastId,
        previewWidgetTaskCollection: {
          id: WidgetTaskCollectionPreviewID,
          taskSections: [
            taskSection({
              index: 0,
              title: intl.formatMessage({
                defaultMessage: "Entrance",
                description: "WATaskCollectionExampleItem 0",
              }),
            }),
            taskSection({
              index: 1,
              title: intl.formatMessage({
                defaultMessage: "Frozen Storage",
                description: "WATaskCollectionExampleItem 1",
              }),
            }),
            taskSection({
              index: 2,
              title: intl.formatMessage({
                defaultMessage: "Printer room",
                description: "WATaskCollectionExampleItem 2",
              }),
            }),
            taskSection({
              index: 3,
              title: intl.formatMessage({
                defaultMessage: "Loading dock",
                description: "WATaskCollectionExampleItem 3",
              }),
            }),
            taskSection({
              index: 4,
              title: intl.formatMessage({
                defaultMessage: "Restaurant",
                description: "WATaskCollectionExampleItem 4",
              }),
            }),
            taskSection({
              index: 5,
              title: intl.formatMessage({
                defaultMessage: "Checkout line",
                description: "WATaskCollectionExampleItem 5",
              }),
            }),
            taskSection({
              index: 6,
              title: intl.formatMessage({
                defaultMessage: "Staff cafeteria",
                description: "WATaskCollectionExampleItem 6",
              }),
              routineTitle: alternativeRoutineTitle,
            }),
            taskSection({
              index: 7,
              title: intl.formatMessage({
                defaultMessage: "Changing room ",
                description: "WATaskCollectionExampleItem 7",
              }),
              routineTitle: alternativeRoutineTitle,
            }),
          ],
          title: intl.formatMessage({
            description: "WATaskCollection main checklist title",
            defaultMessage: "Store Checklist",
          }),
        },
      },
    };
    env.commitPayload(op, payload);
    load(vars);
  }, [env, intl, load, vars]);

  useEffect(() => populateData(), [populateData]);

  return queryRef;
}
